import DesignSystemButton from '../Button/DesignSystemButton';
import InfoFullIcon from '../../assets/images/Icons/Info/InfoFull';
import React from 'react';
import Show from '../Show/Show';
import Tooltip from '../Tooltip/Tooltip';
import { ActivityInfo, ActivityType } from '../ActivityInfo/ActivityInfo';
import { Body1Reg, Caption2Bold, Title2 } from '../../config/designSystem/typography';
import { COLORS, GRADIENTS, colorsWithBrightnessRange } from '../../config/designSystem/Colors';
import { DropShadowMedium } from '../DropShadows/DropShadowMedium';
import { GradientButton } from '../Gradients/GradientButton';
import { GradientProgressBar } from '../Gradients/GradientProgressBar';
import { IPathModel } from '../../state/stores/Path/PathModel';
import { Pressable, View } from 'react-native';
import { SPACING } from '../../config/designSystem/Spacing';
import { branchingKey, chooseNewPathKey, dashboardStackKey, pathKey, privateKey, sessionStackKey } from '../../constants/routeKeys';
import { concatPath } from '../../utils/links/concatPath';
import { isAndroid } from '../../config/designSystem/Layout';
import { logAmplitude } from '../../utils/analytics/analytics';
import { observer } from 'mobx-react';
import { router } from 'expo-router';
import { useStores } from '../../hooks/useStores';

export type ProgramBackgroundProps = {
    pathId?: string;
    startOnboarding: boolean;
    onboardingTransition: boolean;
    onCloseComplete: () => void;
    indexOnboarding: number;
    onNextOnboarding: () => void;
}

const _ProgramCard = ({pathId,startOnboarding,onboardingTransition, onCloseComplete, indexOnboarding, onNextOnboarding}: ProgramBackgroundProps) => {
  const rootStore = useStores();
  const path: IPathModel = rootStore.path.getPath(pathId || null);

  const labels = rootStore?.language.translate.MOBILE?.HOME;
  const programExist = rootStore?.path?.programExist;
  const currentSession = programExist? rootStore?.session?.getCurrentSession(pathId) : null;

  const color =  (path?.designSystemColor as keyof typeof colorsWithBrightnessRange) || 'LIGHT_GREEN';
  const doneToday = rootStore?.path?.getIsDoneToday(pathId);
  const pathFinishedToday = path?.programFinishedToday;
  const nextSessionExist = programExist && !!currentSession && !pathFinishedToday;

  const onboardingInitIndex = 0;
  const onboardingEndIndex = 2;




  const onPressNewPath = () => {
    router.navigate(concatPath([privateKey, dashboardStackKey, chooseNewPathKey], -1));
  };

  const goToPath = () => {
    router.navigate({
      pathname: concatPath([privateKey, dashboardStackKey, pathKey], -1) as string,
      params: {id: pathId}
    });
  };

  const onPressCard=() => {
    if(indexOnboarding>=onboardingInitIndex && indexOnboarding<=onboardingEndIndex){
      onNextOnboarding();
      return;
    }
    if(programExist){
      goToPath();
    } else {
      onPressNewPath();
    }
  };

  const onPressSession = () => {
    if(indexOnboarding>=0 && indexOnboarding<=2){
      onNextOnboarding();
      return;
    }
    if(doneToday){
      goToPath();
    } else if(nextSessionExist) {
      logAmplitude('Session Started', {
        pathId: path?.humanReadableId,
        sessionId: currentSession?.humanReadableId,
        from: 'program_card'
      });
      router.navigate({
        pathname: concatPath([privateKey, sessionStackKey, branchingKey ], -1) as string,
        params: {
          pathId: path?._id,
          sessionId: currentSession?._id,
          fromPath: encodeURIComponent(true),
          fromReckoning: encodeURIComponent(false),
        }});
    }
  };

  const renderBottom = () => {
    return(
      <>
        <Show when={!currentSession}>
          <DesignSystemButton onPress={onPressNewPath} label={labels?.START}/>
        </Show>
        <Show when={!!currentSession}>
          <Show when={pathFinishedToday}>
            <Caption2Bold color={COLORS.content.accent}>
              {labels?.VIEW_SUMMARY}
            </Caption2Bold>
          </Show>
          <Show when={!pathFinishedToday}>
            <Show when={doneToday}>
              <Body1Reg align={true} color={COLORS.content['primary']} >
                {labels?.YOU_SMASHED_IT}
              </Body1Reg>
            </Show>
            <Show when={!doneToday}>
              <Tooltip
                isVisible={!onboardingTransition && indexOnboarding==1}
                from={(
                  <Pressable onPress={() => {}}>
                    <GradientButton onPress={onPressSession} label={labels?.CONTINUE} gradient={GRADIENTS['modules'][color]['base']}/>
                  </Pressable>                )}
                placement="bottom"
                onClose={() => onNextOnboarding()}
                onCloseComplete={onCloseComplete}
              >
                <Body1Reg> {labels?.ONBOARDING_STEP_1}</Body1Reg>
              </Tooltip>
            </Show>

          </Show>
        </Show>
      </>

    );

  };
  const renderCardContent = () => {
    return(
      <View style={{rowGap: SPACING['sp-16'], alignItems: 'center'}}>
        {renderUpperCardContent()}
        {renderBar()}
        {renderBottom()}
      </View>
    );
  };

  const renderBar = () => {
    const percentage = (100*((currentSession?.order || 1) -1 )/ (path?.totalNumberSessions || 4)) || 2;
    if(programExist) {
      return(
        <GradientProgressBar gradient={GRADIENTS['modules'][color]['base']} readPercentage={(doneToday || pathFinishedToday)? 100: percentage}  />

      );
    } else {
      return(<View style={{height: SPACING['sp-4'], width: '100%', backgroundColor: COLORS.surface['secondary']}} />);
    }
  };

  const renderUpperCardContent = () => {
    const upperText = !programExist ? labels?.CHOOSE_YOUR_PATH : path?.title;
    const middleText = pathFinishedToday
      ? labels?.YOU_ROCK.replace('{{name}}', rootStore?.user?.user?.name)
      : !programExist
        ? labels?.GET_YOUR_TAILORED_PROGRAM
        : currentSession?.title;
    const colorForSubtitle = programExist
      ? colorsWithBrightnessRange[color]['500']
      : COLORS.content.accent;
    return (
      <View style={{ rowGap: SPACING['sp-8'], alignItems: 'center' }}>
        <View style={{flexDirection: 'row', columnGap: SPACING['sp-8']}}>
          <Caption2Bold color={colorForSubtitle}>{upperText}</Caption2Bold>
          <Show when={programExist} >
            <InfoFullIcon color={colorForSubtitle}/>
          </Show>
        </View>

        <Title2 align={true}>{middleText}</Title2>
        <Show when={nextSessionExist}>

          <View style={{backgroundColor: COLORS.surface['contrast']}}>
            <ActivityInfo type={ActivityType.LESSON} />
          </View>
        </Show>
        <Show when={pathFinishedToday}>
          <Body1Reg color={COLORS.content['primary']}>
            {labels?.YOU_COMPLETED_YOUR_PATH_SUCCESSFULLY}
          </Body1Reg>
        </Show>
      </View>
    );
  };





  return(
    <Tooltip
      isVisible={!onboardingTransition && (
        (startOnboarding && !!pathId && programExist && nextSessionExist && !doneToday) || indexOnboarding === 2)}
      from={(
        <Pressable style={[{
          width:'100%'}
        ,
        !isAndroid && {alignSelf:'center',
          maxWidth: 800}]}

        onPress={onPressCard} >
          <DropShadowMedium>
            <View style={{padding: SPACING['sp-24'],
              borderRadius: SPACING['sp-12'],
              backgroundColor: COLORS.surface['contrast'],
            }} >
              {renderCardContent()}
            </View>
          </DropShadowMedium>
        </Pressable>
      )}
      placement={'bottom'}
      onClose={onNextOnboarding}
      onCloseComplete={onCloseComplete}
      // allowChildInteraction={false}
      // contentStyle={contentTooptipStyle}
      // tooltipStyle={tooptipStyle}
    >
      <View><Body1Reg> {indexOnboarding === 0? labels?.ONBOARDING_STEP_0: labels?.ONBOARDING_STEP_2}</Body1Reg></View>
    </Tooltip>
  );
};

export const ProgramCard = observer(_ProgramCard);